import { CHAIR_CAMERA_DIMS } from './constants.js';

/**
 * VapiChair - Avatar/chair component for VAPI voice AI agent.
 * Same dimensions as VapiCamera (160×140). When clicked, starts the user's
 * camera and connects to VAPI; the stream is displayed in VapiCamera.
 * Implements updatePoster(mesh) to sync with drag like VRIframeAgentGhost.
 */
class VapiChair {

    static resolveScenarioLabel(dbConfig = {}) {
        const custom = (dbConfig?.chairScenario != null ? String(dbConfig.chairScenario) : '').trim();
        if (custom) return custom;
        const nameFallback = (dbConfig?.chairName != null ? String(dbConfig.chairName) : '').trim();
        if (nameFallback) return nameFallback;
        return (typeof $trans !== 'undefined' && $trans('vrai-agent-scenario-default')) || 'Voice Assistant';
    }

    static formatLabelFontSizeValue(rem) {
        if (rem == null || rem === '' || !Number.isFinite(Number(rem))) return '';
        return `calc(${Number(rem)}rem * var(--vrai-chair-proportion-scale, 1))`;
    }

    static getDefaultChairImgSrc() {
        return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 180'%3E%3Crect fill='%23333' width='200' height='180'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23666' font-size='48'%3E%F0%9F%A4%96%3C/text%3E%3C/svg%3E";
    }

    constructor(options = {}) {
        const [w, h] = CHAIR_CAMERA_DIMS;
        this.dbConfig = options.dbConfig || {};
        this.width = options.width ?? w;
        this.height = options.height ?? h;
        /** Scene mesh id for `.indicator.indicator-${id}` (set by VRVapiChairWithCSS3D). */
        this.meshId = options.meshId != null ? options.meshId : null;
        window.chair = this;
    }

    render() {
        const chairLabel = this.dbConfig?.chairName ?? ((typeof $trans !== 'undefined' && $trans('vrai-agent-text')) || 'AI Agent');
        const scenarioLabel = VapiChair.resolveScenarioLabel(this.dbConfig);
        const talkToAgentLabel = ((typeof $trans !== 'undefined' && $trans('vrai-agent-talk-to-agent-aria')) || 'Talk to AI');
        const endCallLabel = (typeof $trans !== 'undefined' && $trans('action-end-call')) || 'End call';
        const endLabel = endCallLabel;
        const agentImage = this.dbConfig?.agent_image;
        const chairImgSrc = (agentImage && !agentImage.includes('empty')) ? agentImage : VapiChair.getDefaultChairImgSrc();
        const nameSizeVal = VapiChair.formatLabelFontSizeValue(this.dbConfig?.chairNameSize);
        const scenarioSizeVal = VapiChair.formatLabelFontSizeValue(this.dbConfig?.chairScenarioSize);
        const nameSize = nameSizeVal ? `font-size:${nameSizeVal};` : '';
        const scenarioSize = scenarioSizeVal ? `font-size:${scenarioSizeVal};` : '';
        const mid = this.meshId != null ? String(this.meshId) : '';
        const indicatorBlock = mid
            ? `<div class="indicator chair-indicator indicator-${mid} vrai-agent-chair-indicator-el" data-indicator="${mid}" aria-hidden="true">
                <div class="circle-wrapper">
                    <span class="material-icons-outlined">record_voice_over</span>
                </div>
                <span class="tooltip" data-lang="vrai-agent-text">${chairLabel}</span>
            </div>`
            : '';

        const div = document.createElement('div');
        div.setAttribute('class', 'vrai-component-slot vrai-agent-app');
        div.setAttribute('data-vrai-component', 'chair');
        div.innerHTML = `
            <div class="vrai-component-content" style="position:absolute;inset:0;overflow:visible;">
                <main class="vrai-agent-view vrai-agent-grid" data-view="grid">
                    <div class="vrai-agent-chair-slot">
                        <div class="status-side status-agent vrai-status-agent" aria-live="polite"></div>
                        <div class="vrai-agent-chairs">
                            <div class="vrai-agent-chair" data-chair="0" role="button" tabindex="0" aria-label="${talkToAgentLabel}">
                                <img class="vrai-agent-chair-img" src="${chairImgSrc}" alt="" draggable="false" />
                                <button type="button" class="vrai-agent-btn-end vrai-btn-end vrai-agent-chair-btn-end hidden" style="display:none" aria-hidden="true" tabindex="-1">${endLabel}</button>
                                <div class="vrai-agent-chair-timer vrai-agent-timer vrai-timer">00:00</div>
                            ${indicatorBlock}
                            <button type="button" class="indicator vrai-agent-chair-end-indicator vrai-agent-chair-end-indicator-el" aria-label="${endCallLabel}" tabindex="-1">
                                <div class="circle-wrapper">
                                    <span class="material-icons-outlined" aria-hidden="true">call_end</span>
                                </div>
                            </button>
                            <div class="vrai-agent-chair-footer">
                                <div class="vrai-agent-chair-name" style="${nameSize}">${chairLabel}</div>
                                <div class="vrai-agent-chair-scenario" style="${scenarioSize}">${scenarioLabel}</div>
                            </div>
                        </div>
                    </div>
                    <div class="vrai-agent-call-controls hidden"></div>
                </main>
            </div>
        `;
        this._root = div;
        return div;
    }

    getContentElement() {
        return this._root?.querySelector('.vrai-component-content');
    }

    updateImage(src) {
        const img = this._root?.querySelector('.vrai-agent-chair-img');
        if (img && src !== undefined) img.src = src || VapiChair.getDefaultChairImgSrc();
    }

    updateChairLabels(name, scenario) {
        const nameEl = this._root?.querySelector('.vrai-agent-chair-name');
        const scenarioEl = this._root?.querySelector('.vrai-agent-chair-scenario');
        if (nameEl && name !== undefined) nameEl.textContent = name || (typeof $trans !== 'undefined' && $trans('vrai-agent-text')) || 'AI Agent';
        if (scenarioEl && scenario !== undefined) {
            scenarioEl.textContent = VapiChair.resolveScenarioLabel({ chairScenario: scenario });
        }
    }

    updateTextSizes(nameRem, scenarioRem) {
        const nameEl = this._root?.querySelector('.vrai-agent-chair-name');
        const scenarioEl = this._root?.querySelector('.vrai-agent-chair-scenario');
        if (nameEl && nameRem !== undefined) nameEl.style.fontSize = VapiChair.formatLabelFontSizeValue(nameRem) || '';
        if (scenarioEl && scenarioRem !== undefined) scenarioEl.style.fontSize = VapiChair.formatLabelFontSizeValue(scenarioRem) || '';
    }

    /** Legacy: size is fixed globally via `.indicator` + CSS3D inverse scale (see VRVapiChairWithCSS3D). */
    updateIndicatorSize() { /* no-op */ }

    /** Sync poster position/rotation with dragged mesh (like VRIframeAgentGhost.updatePoster). */
    updatePoster(posterObj, mesh) {
        if (!posterObj || !mesh) return;
        posterObj.position.copy(mesh.position);
        posterObj.quaternion.copy(mesh.quaternion);
        posterObj.rotation.copy(mesh.rotation);
        posterObj.scale.copy(mesh.scale);
    }
}

export { VapiChair };
